import { useMemo, useState } from "react";
import { ProcedimentoBundle, OpmeBundle, Referencia, ItemRecurso } from "../../types/resources.types";
import { useSession } from "../../context/SessionContext";
import { useApi } from "../../hooks/useApi";
import { ReferenceTarget, referencesService, summaryReferencesPayload } from "../../services/references";
import { resolveMessage, toAppError } from "../../lib/feedback";

/** Link de conferência de uma referência (busca pelo título). */
export const referenceUrl = (r: Referencia) =>
  r.titulo ? `https://www.google.com/search?q=${encodeURIComponent(r.titulo)}` : null;

/** Sem referências, libera. Com referências: ao menos uma incluída, todos os links conferidos e a declaração aceita. */
export const canConfirmReferences = (s: { hasRefs: boolean; selected: number; opened: number; total: number; declared: boolean }) =>
  !s.hasRefs || (s.selected > 0 && s.opened === s.total && s.declared);

export function useReferences() {
  const { metadata } = useSession();
  const { apiFetch } = useApi();

  const [procedimentos, setProcedimentos] = useState<ProcedimentoBundle[]>([]);
  const [opmes, setOpmes] = useState<OpmeBundle[]>([]);
  const [selectedRefs, setSelectedRefs] = useState<Record<number, boolean>>({});
  const [openedUrls, setOpenedUrls] = useState<Set<string>>(new Set());
  const [declared, setDeclared] = useState(false);

  const [loadingRefs, setLoadingRefs] = useState(false);
  const [errorRefs, setErrorRefs] = useState<string | null>(null);
  const [loadingProc, setLoadingProc] = useState(false);
  const [loadingOpme, setLoadingOpme] = useState(false);

  const hydrateFromBackend = (result: { procedimentos?: ProcedimentoBundle[]; opmes?: OpmeBundle[] }) => {
    if (result.procedimentos !== undefined) setProcedimentos(result.procedimentos);
    if (result.opmes !== undefined) setOpmes(result.opmes ?? []);
    const newRefs = [...(result.procedimentos ?? []), ...(result.opmes ?? [])].flatMap((x) => x.referencias);
    if (newRefs.length > 0) {
      setSelectedRefs((prev) => {
        const next = { ...prev };
        for (const r of newRefs) next[r.id] = true;
        return next;
      });
    }
  };

  const selectedReferenciasFlat = useMemo(
    () => [...procedimentos, ...opmes].flatMap((x) => x.referencias).filter((r) => r && selectedRefs[r.id]),
    [procedimentos, opmes, selectedRefs]
  );

  // Só contam as referências com link para conferir.
  const withLink = selectedReferenciasFlat.filter((r) => referenceUrl(r));
  const openedCount = withLink.filter((r) => openedUrls.has(referenceUrl(r)!)).length;
  const hasRefs = procedimentos.length > 0 || opmes.length > 0;
  const canConfirm = canConfirmReferences({
    hasRefs,
    selected: selectedReferenciasFlat.length,
    opened: openedCount,
    total: withLink.length,
    declared,
  });

  const markRefOpened = (url: string) => setOpenedUrls((prev) => new Set(prev).add(url));
  const toggleSelectRef = (refId: number) => setSelectedRefs((prev) => ({ ...prev, [refId]: !prev[refId] }));
  const toggleDeclaracao = () => setDeclared((prev) => !prev);

  // O erro fica no próprio diálogo (com "Tentar de novo"), por isso não passa pelo toast do useApi.
  const reloadReferencias = async (descricao_paciente: string, itensSelecionados: ItemRecurso[], alvoTipo?: ReferenceTarget) => {
    const setLoading = alvoTipo === "CODIGO" ? setLoadingProc : alvoTipo === "MATERIAL" ? setLoadingOpme : setLoadingRefs;
    setLoading(true);
    setErrorRefs(null);
    try {
      const payload = summaryReferencesPayload(descricao_paciente, itensSelecionados, metadata.email, alvoTipo);
      const data = await referencesService.summary(apiFetch, payload, alvoTipo);
      // Só atualiza o tipo pedido.
      hydrateFromBackend({
        ...(alvoTipo !== "MATERIAL" && { procedimentos: data?.referencias?.procedimentos ?? [] }),
        ...(alvoTipo !== "CODIGO" && { opmes: data?.referencias?.opmes ?? [] }),
      });
    } catch (err) {
      setErrorRefs(resolveMessage(toAppError(err)).body);
    } finally {
      setLoading(false);
    }
  };

  const resetState = () => {
    setProcedimentos([]);
    setOpmes([]);
    setSelectedRefs({});
    setOpenedUrls(new Set());
    setDeclared(false);
  };

  return {
    procedimentos,
    opmes,
    hasRefs,
    selectedRefs,
    toggleSelectRef,
    selectedReferenciasFlat,
    markRefOpened,
    openedUrls,
    openedCount,
    linkCount: withLink.length,
    declared,
    toggleDeclaracao,
    loadingRefs,
    errorRefs,
    loadingProc,
    loadingOpme,
    reloadReferencias,
    canConfirm,
    resetState,
  };
}
